import React, { FunctionComponent, ReactNode } from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { AlertLoudIcon } from '../../atoms/icons/AlertLoudIcon'
import { CheckIcon } from '../../atoms/icons/CheckIcon'
import { Link } from '../../atoms/link/Link'
import { Title, Size as TitleSize } from '../../atoms/title/Title'
import { SafetyPlanRow } from '../../molecules/safetyPlanRow/SafetyPlanRow'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export type RiskSummaryItem = {
  id: string
  title: ReactNode
  source: string
  onSourcePress?: () => void
  timestamp?: ReactNode
  requiresAttention: boolean
}

export type RiskSummaryCardProps = {
  risks: RiskSummaryItem[]
  safetyPlan?: { createdDate: ReactNode; createdBy?: ReactNode; isDraft?: boolean }
  onSafetyPlanPress: () => void
  onViewAllPress?: () => void
  testID?: string
}

const Card = styled.View(({ theme: { colors, spacing } }) => ({
  backgroundColor: colors.backgroundPrimary,
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: colors.borderDefault,
  borderRadius: spacing['16px'],
  overflow: 'hidden',
}))

const Header = styled.View(({ theme: { colors, spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: colors.backgroundSecondary,
  borderBottomWidth: 1,
  borderBottomColor: colors.borderDefault,
  paddingVertical: spacing['16px'],
  paddingHorizontal: spacing['24px'],
}))

const Content = styled.View(({ theme: { spacing } }) => ({
  gap: spacing['16px'],
  paddingVertical: spacing['16px'],
  paddingHorizontal: spacing['24px'],
}))

const RiskRow = styled.View(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['40px'],
}))

const RiskTitle = styled.View(({ theme: { spacing } }) => ({
  flex: 1,
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  columnGap: spacing['16px'],
}))

const StatusIcon = styled.View({
  width: 24,
  height: 24,
  alignItems: 'center',
  justifyContent: 'center',
})

const Status = styled.View(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['4px'],
}))

export const RiskSummaryCard: FunctionComponent<RiskSummaryCardProps> = ({
  risks,
  safetyPlan,
  onSafetyPlanPress,
  onViewAllPress,
  testID,
}) => {
  const { colors } = useTheme() as ThemeType
  const { formatMessage } = useIntl()

  return (
    <Card testID={testID ?? tID('RiskSummaryCard')}>
      <Header>
        <Title
          text={formatMessage({
            defaultMessage: 'Risk summary',
            description: 'Header for the client risk summary card',
          })}
          size={TitleSize.SMALL}
        />
        {onViewAllPress && (
          <Link
            text={
              <BodyText
                text={formatMessage({ defaultMessage: 'View all', description: 'Link to view the full risk summary' })}
                size={BodyTextSize.DEFAULT}
                color={colors.textActive}
              />
            }
            onPress={onViewAllPress}
            testID={tID('RiskSummaryCard-viewAll')}
          />
        )}
      </Header>
      <Content>
        {risks.length === 0 && (
          <BodyText
            testID={tID('RiskSummaryCard-noRisks')}
            text={formatMessage({
              defaultMessage: 'No presenting risks',
              description: 'Shown in the risk summary card when the client has no risks',
            })}
            size={BodyTextSize.DEFAULT}
            color={colors.textSecondary}
          />
        )}
        {risks.map(({ id, title, source, onSourcePress, timestamp, requiresAttention }) => {
          const sourceText = onSourcePress ? (
            <Link
              text={source}
              size={BodyTextSize.SMALL}
              underline
              onPress={onSourcePress}
              testID={`RiskSummaryCard-source-${id}`}
            />
          ) : (
            <BodyText
              text={source}
              size={BodyTextSize.SMALL}
              color={colors.textSecondary}
              testID={tID(`RiskSummaryCard-source-${id}`)}
            />
          )
          return (
            <RiskRow key={id} testID={tID(`RiskSummaryCard-risk-${id}`)}>
              <RiskTitle>
                <BodyText text={title} size={BodyTextSize.DEFAULT} color={colors.textPrimary} />
                {timestamp ? (
                  <BodyText
                    text={formatMessage(
                      {
                        defaultMessage: '{source} • {timestamp}',
                        description: 'Where a client risk was reported and when, e.g. "Assessment • Today • 8:29 AM"',
                      },
                      { source: sourceText, timestamp },
                    )}
                    size={BodyTextSize.SMALL}
                    color={colors.textSecondary}
                  />
                ) : (
                  sourceText
                )}
              </RiskTitle>
              <Status>
                <StatusIcon>
                  {requiresAttention ? (
                    <AlertLoudIcon width={20} height={17.5} fillColor={colors.textError} />
                  ) : (
                    <CheckIcon size={20} fillColor={colors.textSuccess} withCircle />
                  )}
                </StatusIcon>
                <BodyText
                  text={
                    requiresAttention
                      ? formatMessage({
                          defaultMessage: 'Requires attention',
                          description: 'Status for a client risk that has not been addressed yet',
                        })
                      : formatMessage({ defaultMessage: 'Addressed', description: 'Risk addressed status label' })
                  }
                  size={BodyTextSize.SMALL}
                  color={requiresAttention ? colors.textError : colors.textSuccess}
                />
              </Status>
            </RiskRow>
          )
        })}
        <SafetyPlanRow
          createdDate={safetyPlan?.createdDate}
          createdBy={safetyPlan?.createdBy}
          isDraft={safetyPlan?.isDraft}
          onPress={onSafetyPlanPress}
        />
      </Content>
    </Card>
  )
}
